<template>
  <div>
    <ClientOnly>
      <DsSection
        v-if="relatedContentArticles && relatedContentArticles.length > 0"
        :theme="theme"
        :with-space="false"
        class="ds-padding-y-48 ds-md-padding-y-96"
        full-screen
      >
        <KnowledgeHubArticlesGallery
          :title="relatedContent.title"
          :description="relatedContent.description"
          :articles="relatedContentArticles"
          :see-all-label="seeAllLabel"
          :see-all-link="seeAllLink || knowledgeHubArticlesUrl"
        />
      </DsSection>
    </ClientOnly>
  </div>
</template>

<script setup>
import { DsSection } from 'awds'
import { queryRelatedContent } from '../../../core/utils/knowledge-hub-utils'

const { getLocaleIso } = useLocales()

const store = useRoutesStore()
const { locale: localeRef } = useI18n()
const locale = getLocaleIso(localeRef.value)
const route = useRoute()
const { getUrl, getInternalUrl } = useRoutes(store.routes, localeRef)
const { getSlug } = usePageUtils()
const slug = getSlug(route.path)
/**
 * Knowledge Hub Related Content Component
 * Receives a relatedContent input (BlockRelatedContent) together with a label and link for the "See All button"
 * and displays a KnowledgeHubArticlesGallery component featuring the corresponding articles depending
 * on the filter and cherry picked articles on the relatedContent prop.
 * It's always ClientOnly. Makes use of the util function queryRelatedContent() that fetches the articles in the filters
 * only if necessary
 * TODO: use typscript
 * relatedContent should be:
 * {
 id: '',
 title: '',
 description: '',
 filtersQuery: gqlQuery,
 items: []
 }
 *
 * @example
 * ```js
 * import KnowledgeHubRelatedContent from '@/components/knowledge-hub/knowledge-hub-related-content.vue'
 *
 * export default {
 *   components: { KnowledgeHubRelatedContent },
 *   template: `<div>
 *     <KnowledgeHubRelatedContent
 *        :relatedContent="relatedContent"
 *        :see-all-label="aLabel"
 *        :see-all-link="aURL"
 *      />
 *     />
 *   </div>`
 * }
 * ```
 */

const props = defineProps({
  relatedContent: {
    type: Object,
    default: null
  },
  seeAllLabel: {
    type: String,
    default: ''
  },
  seeAllLink: {
    type: String,
    default: ''
  },
  theme: {
    type: String,
    default: 'light'
  }
})

const relatedContentArticles = ref([])

const fetchRelatedContentArticles = async (relatedContent) => {
  const options = {
    getUrl,
    getInternalUrl,
    locale: localeRef.value,
    slug
  }

  const relatedArticles = await queryRelatedContent(relatedContent, options)

  relatedContentArticles.value = relatedArticles
}

const knowledgeHubArticlesUrl = computed(() => {
  const { getRouteByEntry } = useRoutes(store.routes, localeRef)
  // /articles id
  return getRouteByEntry('3kcR7YrWAsdeQAgbxHghYA')?.route || ''
})

watch(props.relatedContent, (newValue) => {
  if (newValue) {
    fetchRelatedContentArticles(newValue)
  }
  return ''
})

onBeforeMount(() => {
  if (props.relatedContent) {
    fetchRelatedContentArticles(props.relatedContent)
  }
})
</script>
